/**
 * F-ONL-N6 — cancelling an accepted online order from the board.
 *
 * THE list, mirrored by onlineCancelReasons in
 * backend/internal/handlers/online_order_cancel.go
 * (TestOnlineCancelReasons_MatchFrontend compares the two). The backend owns
 * what each reason files the voided lines under and what the customer is told.
 */
import type { VoidOutcome } from '@/lib/void-reasons'

export interface OnlineCancelReason {
  code: string
  label: string
}

export const ONLINE_CANCEL_REASONS: readonly OnlineCancelReason[] = [
  { code: 'customer_cancelled', label: "Customer cancelled" },
  { code: 'not_reachable', label: "Couldn't reach the customer" },
  { code: 'refused', label: "Refused at the door" },
  { code: 'bad_address', label: "Couldn't find the address" },
  { code: 'out_of_stock', label: "Item ran out" },
  { code: 'too_slow', label: "Kitchen couldn't make it in time" },
  { code: 'duplicate', label: "Duplicate order" },
  { code: 'other', label: "Other" },
]

export const OTHER_CANCEL_CODE = 'other'
export const CANCEL_NOTE_MAX = 120

/** The reasons a rider brings back; the rest are for an order still in the kitchen. */
const DELIVERY_FAILED_CODES = ['not_reachable', 'refused', 'bad_address', 'customer_cancelled']
const KITCHEN_CANCEL_CODES = ['customer_cancelled', 'out_of_stock', 'too_slow', 'duplicate']

/**
 * A delivery that has left (or been marked delivered) and came to nothing is
 * "Delivery failed"; everything else is "Cancel order". Same endpoint — the
 * words and the reasons offered first are what differ.
 */
export function isDeliveryFailure(order: { fulfillment_type: string; order_status?: string }): boolean {
  return (
    order.fulfillment_type === 'delivery' &&
    (order.order_status === 'out_for_delivery' || order.order_status === 'served')
  )
}

/** The chips to offer, most likely first. "Other" is the note field, not a chip. */
export function cancelReasonChips(deliveryFailure: boolean): OnlineCancelReason[] {
  const codes = deliveryFailure ? DELIVERY_FAILED_CODES : KITCHEN_CANCEL_CODES
  return codes
    .map((code) => ONLINE_CANCEL_REASONS.find((r) => r.code === code))
    .filter((r): r is OnlineCancelReason => !!r)
}

/**
 * What to pre-select for "was the food made?". Once the kitchen has started or
 * finished, it was. An order that has only been sent is left for staff to
 * answer — printed-ticket kitchens never move past it, so it says nothing.
 */
export function suggestedCancelOutcome(orderStatus: string | undefined): VoidOutcome | null {
  if (
    orderStatus === 'preparing' ||
    orderStatus === 'ready' ||
    orderStatus === 'out_for_delivery' ||
    orderStatus === 'served'
  )
    return 'wasted'
  return null
}

/** The cancel can be sent once a reason is chosen, the food question answered, and Other explained. */
export function cancelFormComplete(code: string | null, outcome: VoidOutcome | null, note: string): boolean {
  if (!code || !outcome) return false
  if (code === OTHER_CANCEL_CODE && note.trim() === '') return false
  return note.trim().length <= CANCEL_NOTE_MAX
}

/** Friendly words for the error codes the cancel endpoint returns. */
export function cancelErrorMessage(code: string | undefined, fallback?: string): string {
  switch (code) {
    case 'invalid_manager_pin':
      return 'That PIN is not a manager PIN.'
    case 'online_order_paid':
      return 'Money has been recorded on this order. Refund it at the counter first.'
    case 'order_terminal':
      return 'This order has already been closed.'
    case 'online_order_not_cancellable':
      return 'This order can no longer be cancelled from here.'
    case 'synced_invoice_requires_credit_note':
    case 'fiscal_submission_requires_credit_note':
      return 'This order has been filed with FBR — issue a credit note from Reports instead.'
    default:
      return fallback || 'Could not cancel the order. Please try again.'
  }
}
